import React, { useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Theme } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from './Button';
import TextInput from './TextInput';
import Form from './Form';
import { StyledText } from './StyledText';
import { FilterOption, MatchListType, Role } from '../types/types';
import { useUser } from '../contexts';
import { ENT_MATCH_THRESHOLD } from '../lib';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: 800
  },
  dialogAction: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

interface FilterModalProps {
  open: boolean
  filterOptions: FilterOption[]
  onClose: () => void
  onSubmit: (v: FilterOption[]) => void
  listType: MatchListType
}

const FilterModal: React.FC<FilterModalProps> = ({ open, filterOptions, listType, onClose, onSubmit }) => {
  const styles = useStyles();

  const user = useUser();
  const { userRole } = user;

  const showNameFilter = (userRole && userRole === Role.Connector) || listType === MatchListType.sent;
  const minMinMatchScore = ((userRole && userRole === Role.Connector) || (listType !== MatchListType.recommended)) ? 0 : ENT_MATCH_THRESHOLD;

  const initialValues = useMemo(() => ({
    contactName: filterOptions.find((a) => a.key === 'contactName')?.value || '',
    companyName: filterOptions.find((a) => a.key === 'companyName')?.value || '',
    minMatchScore: filterOptions.find((a) => a.key === 'minMatchScore')?.value || minMinMatchScore
  }), [filterOptions, minMinMatchScore]);

  const handleSubmit = (values: { contactName?: string, companyName?: string, minMatchScore?: number }) => {
    const { contactName, companyName, minMatchScore } = values;
    const filterOpts: FilterOption[] = [];
    if (contactName) {
      filterOpts.push({
        key: 'contactName',
        value: contactName,
        query: `contactName=${contactName}`,
        label: `Investor Name: ${contactName}`
      });
    }
    if (companyName) {
      filterOpts.push({
        key: 'companyName',
        value: companyName,
        query: `companyName=${companyName}`,
        label: `Company: ${companyName}`
      });
    }
    if (minMatchScore) {
      filterOpts.push({
        key: 'minMatchScore',
        value: minMatchScore,
        query: `minMatchScore=${minMatchScore / 100}`,
        label: `Match Score >= ${minMatchScore}%`
      });
    }
    onSubmit(filterOpts);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      contactName: Yup.string().optional().nullable(),
      companyName: Yup.string().optional().nullable(),
      minMatchScore: Yup.number().min(minMinMatchScore).optional().nullable()
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit
  });

  return (
    <Dialog open={open} onClose={onClose} className={styles.root}>
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Filters</StyledText>
        </DialogTitle>
        <DialogContent>
          { showNameFilter
            ? (
              <TextInput
                id='contactName'
                label='Contact Name'
                type='text'
                placeholder=''
                fullWidth
              />
            )
            : null }
          <TextInput
            id='companyName'
            label='Company Name'
            type='text'
            placeholder=''
            fullWidth
          />
          <TextInput
            id='minMatchScore'
            label={`Min Match Score (${minMinMatchScore} - 100%)`}
            type='number'
            placeholder='80'
            fullWidth
          />
        </DialogContent>
        <DialogActions className={styles.dialogAction}>
          <Button type='button' variant='text' onClick={onClose}>Close</Button>
          <Button type='submit'>Filter</Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default FilterModal;
